import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { readPage } from '../../../utils/apiResult';
import { TAGS_ROOT, TAG_NAME_MAX } from '../../ats-candidates/config/tagApi';
import TagChips from '../../ats-candidates/TagChips';

// Aday etiketi kataloğu yönetimi (ATS Ayarları > Aday Etiketleri). Uçlar
// `v1/ats-candidate-tags` (yazma AT.Write). Ad kiracıda tekildir (büyük/küçük harf
// duyarsız) — mükerrer adı sunucu i18n anahtarıyla reddeder, mesajı interceptor gösterir.
export const DEFAULT_TAG_COLOR = '#0291EF';

export const buildTagPayload = (values) => ({
  name: (values.name || '').trim().slice(0, TAG_NAME_MAX),
  color: values.color || DEFAULT_TAG_COLOR,
});

const writeOk = (res) => res?.data?.status !== false;

const useCandidateTagsApi = (queryParams) => {
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`${TAGS_ROOT}?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: p } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(p);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (values) => {
    try {
      const res = await api.post(TAGS_ROOT, buildTagPayload(values));
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const update = useCallback(async (id, values) => {
    try {
      const res = await api.put(`${TAGS_ROOT}/${id}`, buildTagPayload(values));
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      const res = await api.delete(`${TAGS_ROOT}/${id}`);
      if (!writeOk(res)) return false;
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('ats.candidateTag.name'),
      options: {
        filter: true, sort: true, sortKey: 'name',
        filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'color',
      label: t('ats.candidateTag.color'),
      options: {
        filter: false,
        sort: false,
        // Rengin kendisiyle boyanmış rozet — ad listede zaten var, burada renk görünür.
        customBodyRender: (v) => <TagChips tags={[{ id: 'preview', name: v || DEFAULT_TAG_COLOR, color: v }]} />,
      },
    },
    {
      name: 'usageCount',
      label: t('ats.candidateTag.usageCount'),
      options: { filter: false, sort: false, customBodyRender: (v) => v ?? 0 },
    },
  ], [t]);

  const objectSchema = useMemo(() => [
    {
      name: 'name',
      label: t('ats.candidateTag.name'),
      type: 'text',
      grid: 12,
      required: true,
      description: t('ats.candidateTag.nameHint', { max: TAG_NAME_MAX }),
    },
    { name: 'color', label: t('ats.candidateTag.color'), type: 'colorPicker', grid: 12, defaultValue: DEFAULT_TAG_COLOR },
  ], [t]);

  return {
    fetching, loadFailed, data, pagination, columns, objectSchema, refresh, create, update, remove,
  };
};

export default useCandidateTagsApi;
